import { getLoggerInstance } from '@idmc-ms-fabric/common';
import { CoreAttr, CoreClz } from '@infa-hawk/models';
import { ISchedule, ServiceRegistry } from '@infa-hawk/services';
import type { ICatalogSource } from '@infa-service/cdgc-catalog';
import { contentPublishApi, PublishRequestElementType, PublishRequestIdentityType, PublishRequestOperation } from '@infa-service/mdp-content';
import type { ITask } from '@infa-service/mdp-orchestration-api';
import { jobMonitorControllerApi, JobStatus, taskLifecycleManagementControllerApi } from '@infa-service/mdp-orchestration-api';
import { v4 } from 'uuid';

import catalogSourceBuilder from './catalog-source/catalog-source-builder';
import constants from './constants';
import { getItemType } from './manifest-utils';

export const searchCSFromOrigin = async (origin: string) => {
	const { search } = ServiceRegistry.get();
	return search.elasticService.execute({
		query: {
			bool: {
				must: [{ terms: { elementType: ['OBJECT'] } }],
				filter: [
					{
						terms: {
							[CoreAttr.Origin]: [origin],
						},
					},
					{
						terms: {
							[CoreAttr.ClassType]: [CoreClz.Resource],
						},
					},
				],
			},
		},
	});
};
export const searchCatalogFromExternalURIs = async ({ workspaceId, artifactType, artifactId }: FabricDetails) => {
	const { search } = ServiceRegistry.get();
	const logger = getLoggerInstance({});
	try {
		return search.elasticService.execute({
			query: {
				bool: {
					must: [{ terms: { elementType: ['OBJECT'] } }],
					filter: [
						{
							terms: {
								[constants.EXTERNAL_URI]: [getFabricUniqueSearchText({ workspaceId, artifactType, artifactId })],
							},
						},
						{
							terms: {
								[CoreAttr.ClassType]: [CoreClz.Resource],
							},
						},
					],
				},
			},
		});
	} catch (error) {
		logger.error('Fetching Catalogue source linked with Artifact failed.', { error });
		throw error;
	}
};
type FabricDetails = { workspaceId: string; artifactType: string; artifactId: string };
export const getFabricUniqueSearchText = ({ workspaceId, artifactType, artifactId }: FabricDetails) => `fabric://${workspaceId}/${artifactType}/${artifactId}`;
export const getFabricDetails = (externalURIs: string): FabricDetails => {
	const fabricExternalRegex = new RegExp(/^fabric?:\/\/(?!.*:\/\/)\S+/g);
	let fabricDetails;
	if (fabricExternalRegex.test(externalURIs)) {
		const externalURIPathname = externalURIs.split('fabric://')[1].split('/');
		fabricDetails = {
			workspaceId: externalURIPathname[0],
			artifactType: externalURIPathname[1],
			artifactId: externalURIPathname[2],
		};
	} else {
		throw new Error('Invalid External URI format');
	}
	return fabricDetails;
};

export const saveFabricDetailsToCS = async ({
	catalogSrcId,
	dqArtifactId,
	selectedArtifactId,
	...fabricDetails
}: { catalogSrcId: string; dqArtifactId: string; selectedArtifactId: string } & Omit<FabricDetails, 'artifactId'>) => {
	const csObject = await searchCSFromOrigin(catalogSrcId);
	const artifactName = getItemType();
	/**
	 * *fabricSelectedArtifactURI  = fabric://<worksapceId>/artifactType/<selected warehouse/lakehouse id>
	 */
	const fabricSelectedArtifactURI = getFabricUniqueSearchText({ artifactId: selectedArtifactId, ...fabricDetails });
	/**
	 * *fabricSelectedArtifactURI = fabric://<workspaceId>/Org.informatica.dataQualityArtifact/<created DqArtifactId>
	 */
	const fabricCreatedDQArtifactURI = getFabricUniqueSearchText({
		workspaceId: fabricDetails.workspaceId,
		artifactType: artifactName,
		artifactId: dqArtifactId,
	});
	const response = await contentPublishApi.publishContent({
		xINFAPRODUCTID: import.meta.env.VITE_APP_CLIENT_ID,
		bulkPublishRequest: {
			items: [
				{
					elementType: PublishRequestElementType.Object,
					operation: PublishRequestOperation.Update,
					type: CoreClz.Resource,
					identity: csObject.hits[0]?.[CoreAttr.Identity],
					identityType: PublishRequestIdentityType.Internal,
					attributes: {
						[constants.EXTERNAL_URI]: [fabricSelectedArtifactURI, fabricCreatedDQArtifactURI],
					},
					attributeUpdateOperationTypeMap: {
						[constants.EXTERNAL_URI]: 'OVERWRITE',
					},
				},
			],
		},
	});

	if (response.items && response.items?.length > 0) {
		response.items.forEach((item) => {
			if (item.messageCode === 'CONTENT_FAILED') {
				throw new Error('publishing relationship failed');
			}
		});
	}

	return response;
};
export const getTablesForCS = async (csId: string) => {
	const { search } = ServiceRegistry.get();
	const result = await search.elasticService.execute({
		from: 0,
		size: 1000,
		query: {
			bool: {
				must: [
					{
						terms: {
							elementType: ['OBJECT'],
						},
					},
				],

				filter: [
					{
						terms: {
							[CoreAttr.Origin]: [csId],
						},
					},
					{
						terms: {
							type: [CoreClz.DataSet],
						},
					},
				],
			},
		},
		post_filter: {
			bool: {
				filter: [],
			},
		},
		sort: [
			{
				[CoreAttr.Name]: {
					order: 'asc',
				},
			},
		],
	});
	return result;
};

export const findCSByFabricDetails = async (fabricDetails: FabricDetails) => {
	const { search } = ServiceRegistry.get();
	const result = await search.elasticService.getObjects({
		find: {
			text: getFabricUniqueSearchText(fabricDetails),
			fields: [constants.EXTERNAL_URI],
		},
	});
	return result.hits[0];
};

export const findAssetByCatalogExternalId = async (externalId: string) => {
	const { search } = ServiceRegistry.get();
	const response = await search.elasticService.getObjects({
		find: {
			text: externalId,
			fields: [CoreAttr.ExternalId],
		},
	});
	return response;
};
export const buildLocation = (catalogSource: string, path: string[]): string => `${catalogSource}://${catalogSource}/${path.join('/')}`;

type SearchDetails = { catalogSource: string; path: string };
export const searchPathWithinCS = async ({ catalogSource, path }: SearchDetails) => {
	const { search } = ServiceRegistry.get();
	const location = buildLocation(catalogSource, [path]);
	return search.elasticService.getAssetsByLocations([`${location}`]);
};

function roundTime(date: Date) {
	let min = date.getMinutes();
	min = Math.ceil(min / 5) * 5;
	date.setMinutes(min);
	date.setSeconds(0, 0);
	return date;
}

function getMinutelyRoundedTime(date: Date = new Date(), minutes = 15) {
	let hr = date.getHours();
	let min = date.getMinutes();
	const interval = [];
	let last = 0;
	for (let i = minutes; i <= 60; i += minutes) {
		interval.push({ lower: last, upper: i });
		last = i;
	}
	min = interval.find((x) => min > x.lower && min < x.upper)?.upper || min;
	if (min === 60) {
		min = 0;
		hr++;
	}
	date.setHours(hr);
	date.setMinutes(min);
	return date;
}

export const deleteSchedule = async (id: string) => {
	const { schedule } = ServiceRegistry.get();
	return await schedule.deleteSchedule({ id });
};

export const createSchedule = async (scannerName: string, orgId: string) => {
	/**
	 * Default schedule configuration for fabric
	 */
	const schedule: ISchedule = {
		name: `MSFabric_${scannerName}_${v4()}`,
		description: `Exclusive schedule for Catalog Source : ${scannerName}`,
		start: roundTime(getMinutelyRoundedTime(new Date())).toISOString(),
		timeZoneId: 'America/Los_Angeles', //?@TODO have to check which time zone, in mcc they are using users time zone ?
		frequency: 1,
		intervalType: 'Weekly',
		mon: false,
		tue: false,
		wed: false,
		thu: false,
		fri: false,
		sat: true,
		sun: true,
		weekDay: false,
		dayOfMonth: 0,
		end: null,
		orgId,
		businessHours: null,
		weekOfMonth: 'First',
		dayOfWeek: 'Day',
	};

	return schedule;
};

export const getSchedule = async (id: string) => {
	const { schedule } = ServiceRegistry.get();
	return await schedule.getSchedule({ id });
};

export const getActiveJobStatus = () => [JobStatus.Running, JobStatus.Starting, JobStatus.Submitted];

export const getJobRunningForCS = async (catalogSourceId: string) =>
	await jobMonitorControllerApi.getAllJobs({
		filter: [`status:IN:${getActiveJobStatus().join(',')}`, `type:EQ:Catalog Source Sync`],
		inputProperty: 'catalogSourceId',
		inputPropertyValues: [catalogSourceId],
	});

export const getFilterDetailsFromJob = async (jobId: string) => {
	const resp = await taskLifecycleManagementControllerApi.fetchJobSpec({
		jobId,
	});
	const metadataExtractionTasks =
		resp.taskGroups?.reduce((acc, grp) => [...acc, ...(grp.tasks || ([] as ITask[]))], [] as ITask[]).filter((task) => task.type === 'Metadata Extraction') ||
		[];
	const csSyncConfigs = metadataExtractionTasks
		?.map((task) => (task.payload?.content ? (JSON.parse(task.payload?.content) as ICatalogSource) : null))
		.map((content) => (content !== null ? catalogSourceBuilder.getCatalogSourceByInput(content) : null));

	return csSyncConfigs.reduce((acc, cs) => (cs ? [...acc, ...cs.getFilters()] : acc), [] as string[]);
};

export const getCookie = (name: string) => {
	const nameEQ = `${name}=`;
	const ca = document.cookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) == ' ') {
			c = c.substring(1, c.length);
		}
		if (c.indexOf(nameEQ) == 0) {
			return c.substring(nameEQ.length, c.length);
		}
	}
	return null;
};
